// Template190: Bangkok - Wat Pho (Día 1, mapa + contenido)
// Idéntico a PageWatPho.jsx de guia-bangkok; todos los textos e imágenes vienen de page (backend).
// borderRadius en "12px".

import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { useMapFullscreen } from "../../context/MapFullscreenContext";
import SabiasQue from "../SabiasQue";

const Template190 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const [mapLoaded, setMapLoaded] = useState(false);
	const [fullscreen, setFullscreen] = useState(false);
	const { setMapFullscreen } = useMapFullscreen() || {};

	const headerText = page?.texts?.[0]?.content;
	const intro = page?.texts?.[1]?.content;
	const watPhoTitle = page?.texts?.[2]?.content;
	const watPhoBody = page?.texts?.[3]?.content;
	const rioTitle = page?.texts?.[4]?.content;
	const rioBody = page?.texts?.[5]?.content;
	const tipText = page?.texts?.[6]?.content;

	const mapEmbedUrl = page?.data?.mapEmbedUrl;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found?.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found?.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const watphoImage = getImageSrc("watpho");
	const budaReclinadoImage = getImageSrc("buda-reclinado");
	const stickerUrl = getCanvasImageUrl("sticker");
	const logoImage = getCanvasImageUrl("logo");

	const sabiasQue = Array.isArray(page?.data?.sabiasQue)
		? page.data.sabiasQue
		: [];
	const sabiasQueRio = Array.isArray(page?.data?.sabiasQueRio)
		? page.data.sabiasQueRio
		: [];

	const actualHeaderColor = headerBackgroundColor || "#E8A838";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: "#444",
		margin: 0,
	};

	useEffect(() => {
		const style = document.createElement("style");
		style.innerHTML = `
			@keyframes template190-spin {
				0% { transform: rotate(0deg); }
				100% { transform: rotate(360deg); }
			}
		`;
		document.head.appendChild(style);
		return () => document.head.removeChild(style);
	}, []);

	useEffect(() => {
		if (typeof setMapFullscreen === "function") {
			setMapFullscreen(fullscreen);
			return () => setMapFullscreen(false);
		}
	}, [fullscreen, setMapFullscreen]);

	if (fullscreen && mapEmbedUrl) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: "#E8A838",
					}}
				>
					<Box
						component="button"
						onClick={() => setFullscreen(false)}
						sx={{
							backgroundColor: "#fff",
							color: "#E8A838",
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
						}}
					>
						← Volver a la guía
					</Box>
				</Box>
				<Box
					component="iframe"
					title="Mapa grande"
					src={mapEmbedUrl}
					width="100%"
					sx={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				/>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{headerText}
				{logoImage && !isSmallScreen && (
					<Box
						component="img"
						src={logoImage}
						alt=""
						sx={{
							width: 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{/* MAPA */}
			<Box data-block-id="map" sx={{ flex: 1, marginTop: "-70px", position: "relative" }}>
				{mapEmbedUrl && !mapLoaded && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
							color: "black",
							fontSize: 14,
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
							marginTop: 3.75,
						}}
					>
						<Box
							sx={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: "4px solid #E8A838",
								borderRadius: "50%",
								animation: "template190-spin 1s linear infinite",
							}}
						/>
					</Box>
				)}

				{mapEmbedUrl ? (
					<Box
						component="iframe"
						title="Mapa Día 1"
						src={mapEmbedUrl}
						width="100%"
						sx={{
							height: isSmallScreen ? "400px" : "480px",
							border: 0,
						}}
						loading="lazy"
						onLoad={() => setMapLoaded(true)}
					/>
				) : (
					watphoImage && (
						<Box
							component="img"
							src={watphoImage}
							alt=""
							sx={{
								width: "100%",
								height: isSmallScreen ? "400px" : "340px",
								objectFit: "cover",
								position: "relative",
								zIndex: 2,
							}}
						/>
					)
				)}

				{mapEmbedUrl && (
					<Box
						component="button"
						onClick={() => setFullscreen(true)}
						sx={{
							position: "absolute",
							top: 80,
							right: 10,
							backgroundColor: "#E8A838",
							color: "#fff",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							textDecoration: "none",
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",
							zIndex: 4,
							border: "none",
							cursor: "pointer",
						}}
					>
						Ver mapa grande
					</Box>
				)}

				{tipText && (
					<Box
						sx={{
							position: "absolute",
							bottom: 80,
							right: 5,
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 0px 16px",
							padding: "10px 12px",
							fontSize: 11,
							color: "#444",
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: 120,
							zIndex: 3,
						}}
					>
						{tipText}
					</Box>
				)}
				{stickerUrl && (
					<Box
						component="img"
						src={stickerUrl}
						alt=""
						sx={{
							width: 110,
							position: "absolute",
							bottom: 0,
							right: -10,
							zIndex: 2,
						}}
					/>
				)}
			</Box>

			<Box data-block-id="text-1" sx={{ padding: "10px 12px", position: "relative", paddingTop: 1.5 }}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					{intro && (
						<Box
							data-block-id="text-2"
							sx={{ fontSize: "13px", color: "#333", margin: 0, padding: 0 }}
						>
							{intro}
						</Box>
					)}

					{/* Wat Pho */}
					<Box data-block-id="text-3" sx={{ display: "flex", gap: 2, position: "relative" }}>
						{budaReclinadoImage && (
							<Box
								component="img"
								src={budaReclinadoImage}
								alt=""
								sx={{
									width: "40%",
									height: 110,
									objectFit: "cover",
									borderRadius: "12px",
								}}
							/>
						)}
						<Box sx={{ width: "60%" }}>
							<Box component="h3" sx={estiloH3}>
								{watPhoTitle}
							</Box>
							{watPhoBody && (
								<Box component="p" sx={estiloP}>
									{watPhoBody}
								</Box>
							)}
						</Box>
						{sabiasQue.length > 0 && (
							<>
								<Box
									sx={{
										position: "absolute",
										top: isSmallScreen ? 30 : 0,
										left: "33%",
									}}
								>
									<SabiasQue
										icono={sabiasQue[0]?.icono ?? "💆"}
										texto={sabiasQue[0]?.texto ?? ""}
									/>
								</Box>
								{sabiasQue[1] && (
									<Box
										sx={{
											position: "absolute",
											top: isSmallScreen ? 70 : 40,
											left: "33%",
										}}
									>
										<SabiasQue
											icono={sabiasQue[1]?.icono ?? "🪙"}
											texto={sabiasQue[1]?.texto ?? ""}
										/>
									</Box>
								)}
							</>
						)}
					</Box>

					{/* Río Chao Phraya */}
					<Box data-block-id="text-4" sx={{ display: "flex", gap: 2, position: "relative" }}>
						{sabiasQueRio.length > 0 && (
							<Box sx={{ position: "absolute", top: -30, left: "30%" }}>
								<SabiasQue
									icono={sabiasQueRio[0]?.icono ?? "🚤"}
									texto={sabiasQueRio[0]?.texto ?? ""}
								/>
							</Box>
						)}
						<Box sx={{ width: "100%" }}>
							<Box
								component="p"
								sx={{
									...estiloP,
									padding: 1.5,
									backgroundColor: "#FFF3CD",
									borderRadius: "12px",
								}}
							>
								<Box component="h3" sx={estiloH3}>
									{rioTitle}
								</Box>
								{rioBody}
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

Template190.thumbnail = "/images/template-1.png";

export default Template190;
